import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import WebsiteLayout from "../layouts/WebsiteLayout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import FavoriteAppUnlockButton from "../components/common/FavoriteAppUnlockButton";
import { vibesColors as colors } from "../theme/vibesColors";
import { buildHomeNewPayload, getUserCategory, postHomeNew } from "../services/apiService";
import { extractNearbyHostsForCards } from "../utils/nearbyHostsHelpers";
import { reverseGeocodeGoogle } from "../utils/googleLocationService";
import { getSavedHomeLocation, hasSavedHomeLocation, saveHomeLocation } from "../utils/homeLocationStorage";
import "./HomePage.css";

const ALL_CATEGORY_ID = "all";
const HOME_NEARBY_PREVIEW_COUNT = 5;

function getStoredUserId() {
  const keys = ["userId", "userid", "_id", "id"];
  const stores = [window.localStorage, window.sessionStorage];
  for (const store of stores) {
    for (const key of keys) {
      const direct = store.getItem(key);
      if (direct) return direct;
    }
    const userRaw = store.getItem("user");
    if (userRaw) {
      try {
        const user = JSON.parse(userRaw);
        const value = user?.userId || user?._id || user?.id || "";
        if (value) return String(value);
      } catch {
        // No-op: invalid user JSON should not break home page.
      }
    }
  }
  return "";
}

function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  const keys = [
    "data",
    "list",
    "items",
    "events",
    "event",
    "club",
    "clubs",
    "host",
    "hosts",
    "result",
    "rows",
    "category",
    "categories",
  ];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return [];
}

function extractCategoryList(res) {
  const source =
    (Array.isArray(res?.data) && res.data) ||
    (Array.isArray(res?.categories) && res.categories) ||
    asArray(res);

  return source.map((item, index) => {
    const id = String(item?._id || item?.id || item?.categoryid || `c-${index}`);
    const name = String(item?.name || item?.title || item?.category || item?.categoryName || `Category ${index + 1}`);
    return { id, name, raw: item };
  });
}

export default function HomePage() {
  const initialLocation = useMemo(() => getSavedHomeLocation(), []);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY_ID);
  const [listing, setListing] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [coords, setCoords] = useState({ lat: initialLocation.lat, long: initialLocation.long });
  const [locationLabel, setLocationLabel] = useState(initialLocation.label);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingListing, setLoadingListing] = useState(true);
  const [error, setError] = useState("");
  const userId = useMemo(() => getStoredUserId(), []);
  const { lat, long } = coords;

  useEffect(() => {
    let alive = true;
    const bootstrapLocation = async () => {
      const saved = getSavedHomeLocation();
      if (!alive) return;
      setCoords({ lat: saved.lat, long: saved.long });
      if (saved.label) setLocationLabel(saved.label);

      const finalizeLocation = async (nextLat, nextLong, fallbackLabel = "") => {
        const resolvedLabel = (await reverseGeocodeGoogle({ lat: nextLat, long: nextLong })) || fallbackLabel;
        if (!alive) return;
        const fallbackFromCoords = `Lat ${Number(nextLat).toFixed(4)}, Long ${Number(nextLong).toFixed(4)}`;
        const nextLocation = {
          lat: String(nextLat || "").trim(),
          long: String(nextLong || "").trim(),
          label: String(resolvedLabel || "").trim() || fallbackFromCoords,
        };
        setCoords({ lat: nextLocation.lat, long: nextLocation.long });
        setLocationLabel(nextLocation.label);
        saveHomeLocation(nextLocation);
      };

      if (hasSavedHomeLocation() && saved.label) return;

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const nextLat = String(position?.coords?.latitude || "").trim();
            const nextLong = String(position?.coords?.longitude || "").trim();
            if (nextLat && nextLong) {
              await finalizeLocation(nextLat, nextLong, saved.label || "");
              return;
            }
            await finalizeLocation(saved.lat, saved.long, saved.label || "Selected location");
          },
          async () => {
            await finalizeLocation(saved.lat, saved.long, saved.label || "Selected location");
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
        );
        return;
      }
      await finalizeLocation(saved.lat, saved.long, saved.label || "Selected location");
    };

    bootstrapLocation();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCategories(true);
      setError("");
      try {
        const res = await getUserCategory();
        if (cancelled) return;
        const normalized = extractCategoryList(res).filter((item) => Boolean(item?.name?.trim?.()));
        setCategories(normalized);
      } catch (e) {
        if (!cancelled) {
          setCategories([]);
          setError(e instanceof Error ? e.message : "Failed to load categories");
        }
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lat, long, userId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingListing(true);
      setError("");
      try {
        const res = await postHomeNew(
          buildHomeNewPayload({
            lat,
            long,
            userId,
            categoryid: activeCategory !== ALL_CATEGORY_ID ? activeCategory : "",
            search_keyword: searchKeyword.trim(),
          })
        );
        if (cancelled) return;
        setListing(extractNearbyHostsForCards(res));
      } catch (e) {
        if (!cancelled) {
          setListing([]);
          setError(e instanceof Error ? e.message : "Failed to load home listing");
        }
      } finally {
        if (!cancelled) setLoadingListing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCategory, lat, long, searchKeyword, userId]);

  const allCategories = useMemo(
    () => [{ id: ALL_CATEGORY_ID, name: "All" }, ...categories],
    [categories]
  );

  const previewListing = useMemo(
    () => listing.slice(0, HOME_NEARBY_PREVIEW_COUNT),
    [listing]
  );

  const listingFiltersForSeeAll = useMemo(
    () =>
      buildHomeNewPayload({
        lat,
        long,
        userId,
        categoryid: activeCategory !== ALL_CATEGORY_ID ? activeCategory : "",
        search_keyword: searchKeyword.trim(),
      }),
    [lat, long, userId, activeCategory, searchKeyword]
  );

  return (
    <WebsiteLayout>
      <section className="home-page-wrap">
        <div className="home-page-inner">
          <div className="home-page-block home-page-block--top">
            <div className="home-page-location-row">
              <span className="home-page-location-pin" aria-hidden>
                <svg viewBox="0 0 24 24" className="home-page-location-pin-icon">
                  <path
                    d="M12 22s-6.5-5.7-6.5-11A6.5 6.5 0 1 1 18.5 11c0 5.3-6.5 11-6.5 11Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <circle cx="12" cy="11" r="2.2" fill="currentColor" />
                </svg>
              </span>
              <Link
                to="/select-location"
                className="home-page-location-link"
                aria-label="Change location"
                title={locationLabel || "Select location"}
              >
                <h1 className="home-page-location-text">{locationLabel || "Select location"}</h1>
              </Link>
            </div>

            <div className="home-page-search-row">
              <input
                type="text"
                className="home-page-search-input"
                placeholder="Search clubs, events, bars"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
            </div>
          </div>

          <div className="home-page-block home-page-block--categories">
            <div className="home-page-section-head">
              <h2>Categories</h2>
            </div>
            <div className="home-page-category-scroll">
              {loadingCategories ? (
                <span className="home-page-chip-placeholder">Loading...</span>
              ) : (
                allCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={`home-page-category-chip ${activeCategory === category.id ? "is-active" : ""}`}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    {category.name}
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="home-page-block home-page-block--nearby">
            <div className="home-page-section-head">
              <h2>Nearby Profile</h2>
              {!loadingListing && listing.length > HOME_NEARBY_PREVIEW_COUNT ? (
                <Link to="/nearby-hosts" state={{ filters: listingFiltersForSeeAll }} className="home-page-see-all-link">
                  See All
                </Link>
              ) : (
                <span className="home-page-section-meta">{listing.length}</span>
              )}
            </div>

            {error ? <ErrorMessage message={error} /> : null}

            {loadingListing ? (
              <LoadingSpinner label="Loading profiles..." />
            ) : (
              <div className="home-page-list">
                {listing.length === 0 ? (
                  <p className="home-page-empty-text">No results found for selected category.</p>
                ) : (
                  previewListing.map((item) => (
                    <article key={item.id} className="home-page-card">
                      <div className="home-page-card-image-wrap">
                        <img src={item.image} alt={item.title} className="home-page-card-image" />
                      </div>
                      <div className="home-page-card-content">
                        <h3>{item.title}</h3>
                        {item.address ? <p className="home-page-card-address">{item.address}</p> : null}
                        {item.description ? <p className="home-page-card-description">{item.description}</p> : null}
                        {item.distance ? <p className="home-page-card-distance">{item.distance}</p> : null}
                      </div>
                      <div className="home-page-card-actions">
                        <FavoriteAppUnlockButton ariaLabel={`Favorite ${item.title}`} />
                        <Link
                          to={item.id ? `/club/${encodeURIComponent(item.id)}` : "/"}
                          state={{ fromHome: true }}
                          className="home-page-card-action"
                          style={{ background: colors.BtnBackground, color: colors.white }}
                          aria-label={`Open ${item.title}`}
                        >
                          →
                        </Link>
                      </div>
                    </article>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}
